import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TablePagination,
} from '@mui/material';
import {
  People,
  EventNote,
  LocalHospital,
  Book,
  Visibility,
  TrendingUp,
  TrendingDown,
  Schedule,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import apiService from '../services/api';
import { PatientStats, VisitStats, MedicineStats, ReferenceStats, Patient, Visit } from '../types';

// Memoized StatCard component
const StatCard = React.memo<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
  onClick?: () => void;
}>(({ title, value, icon, color, subtitle, onClick }) => (
  <Card sx={{ height: '100%', mb: 2 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}20`,
            borderRadius: '50%',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
));

// Memoized MonthlyStatCard component
const MonthlyStatCard = React.memo<{
  title: string;
  currentValue: number | string;
  previousValue: number | string;
  icon: React.ReactNode;
  color: string;
  formatValue?: (value: number | string) => string;
  onClick?: () => void;
}>(({ title, currentValue, previousValue, icon, color, formatValue = (v) => v.toString(), onClick }) => {
  const currentNum = typeof currentValue === 'string' ? parseFloat(currentValue) || 0 : currentValue;
  const previousNum = typeof previousValue === 'string' ? parseFloat(previousValue) || 0 : previousValue;
  const change = currentNum - previousNum;
  const changePercent = previousNum > 0 ? ((change / previousNum) * 100) : 0;
  const isPositive = change >= 0;

  return (
    <Card 
      sx={{ 
        height: '100%', 
        mb: 2,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography color="textSecondary" variant="body2">
            {title}
          </Typography>
          <Box
            sx={{
              backgroundColor: `${color}20`,
              borderRadius: '50%',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color, mb: 1 }}>
          {formatValue(currentNum)}
        </Typography>
        {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isPositive ? (
            <TrendingUp sx={{ color: 'success.main', fontSize: 16 }} />
          ) : (
            <TrendingDown sx={{ color: 'error.main', fontSize: 16 }} />
          )}
          <Typography 
            variant="body2" 
            color={isPositive ? 'success.main' : 'error.main'}
            sx={{ fontWeight: 600 }}
          >
            {isPositive ? '+' : ''}{changePercent.toFixed(1)}% from last month
          </Typography>
        </Box> */}
        <Typography variant="caption" color="textSecondary">
          Previous month: {formatValue(previousNum)}
        </Typography>
      </CardContent>
    </Card>
  );
});

// Memoized OverallStatCard component
const OverallStatCard = React.memo<{
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  formatValue?: (value: number | string) => string;
  onClick?: () => void;
}>(({ title, value, icon, color, formatValue = (v) => v.toString(), onClick }) => {
  const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;

  return (
    <Card 
      sx={{ 
        height: '100%', 
        mb: 2,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography color="textSecondary" variant="body2">
            {title}
          </Typography>
          <Box
            sx={{
              backgroundColor: `${color}20`,
              borderRadius: '50%',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color, mb: 1 }}>
          {formatValue(numValue)}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          All time total
        </Typography>
      </CardContent>
    </Card>
  );
});

// Memoized table row component
const PatientTableRow = React.memo<{ patient: Patient; onView: (id: number) => void }>(
  ({ patient, onView }) => (
    <TableRow key={patient.id}>
      <TableCell>{patient.name}</TableCell>
      <TableCell>{patient.opd}</TableCell>
      <TableCell>{patient.age}</TableCell>
      <TableCell>
        <Chip
          label={patient.gender}
          size="small"
          color={patient.gender === 'Male' ? 'primary' : 'secondary'}
        />
      </TableCell>
      <TableCell>
        <Tooltip title="View Details">
          <IconButton size="small" onClick={() => onView(patient.id)}>
            <Visibility />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  )
);

// Memoized visit table row component
const VisitTableRow = React.memo<{ visit: Visit; onView: (visit: Visit) => void }>(
  ({ visit, onView }) => (
    <TableRow key={visit.id}>
      <TableCell>{visit.patient_name || 'N/A'}</TableCell>
      <TableCell>{format(new Date(visit.datevisit), 'MMM dd, yyyy')}</TableCell>
      <TableCell>₹{visit.fee}</TableCell>
      <TableCell>
      <Tooltip title="View Visit Details">
      <IconButton size="small" onClick={() => onView(visit)}>
            <Visibility />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  )
);

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [stats, setStats] = useState<{
    patients: PatientStats | null;
    visits: VisitStats | null;
    medicines: MedicineStats | null;
    references: ReferenceStats | null;
  }>({
    patients: null,
    visits: null,
    medicines: null,
    references: null,
  });
  const [monthlyStats, setMonthlyStats] = useState<{
    patients: any | null;
    visits: any | null;
  }>({
    patients: null,
    visits: null,
  });
  const [recentPatients, setRecentPatients] = useState<Patient[]>([]);
  const [recentVisits, setRecentVisits] = useState<Visit[]>([]);
  
  // Pending balance modal state
  const [openPendingBalanceModal, setOpenPendingBalanceModal] = useState(false);
  const [pendingBalanceVisits, setPendingBalanceVisits] = useState<Visit[]>([]);
  const [pendingBalanceLoading, setPendingBalanceLoading] = useState(false);
  const [pendingBalancePage, setPendingBalancePage] = useState(0);
  const [pendingBalanceRowsPerPage, setPendingBalanceRowsPerPage] = useState(10);
  const [pendingBalanceTotal, setPendingBalanceTotal] = useState(0);

  const [todayStats, setTodayStats] = useState<VisitStats | null>(null);
  const [todayPatientStats, setTodayPatientStats] = useState<PatientStats | null>(null);
  const [todayFollowUpCount, setTodayFollowUpCount] = useState<number>(0);
  // State for today's pending balance modal
  const [openTodayPendingBalanceModal, setOpenTodayPendingBalanceModal] = useState(false);
  const [todayPendingBalanceVisits, setTodayPendingBalanceVisits] = useState<Visit[]>([]);
  const [todayPendingBalanceLoading, setTodayPendingBalanceLoading] = useState(false);
  const [todayPendingBalancePage, setTodayPendingBalancePage] = useState(0);
  const [todayPendingBalanceRowsPerPage, setTodayPendingBalanceRowsPerPage] = useState(10);
  const [todayPendingBalanceTotal, setTodayPendingBalanceTotal] = useState(0);

  // State for credit modals
  const [openTodayCreditModal, setOpenTodayCreditModal] = useState(false);
  const [todayCreditVisits, setTodayCreditVisits] = useState<Visit[]>([]);
  const [todayCreditLoading, setTodayCreditLoading] = useState(false);
  const [todayCreditPage, setTodayCreditPage] = useState(0);
  const [todayCreditRowsPerPage, setTodayCreditRowsPerPage] = useState(10);
  const [todayCreditTotal, setTodayCreditTotal] = useState(0);

  const [openMonthCreditModal, setOpenMonthCreditModal] = useState(false);
  const [monthCreditVisits, setMonthCreditVisits] = useState<Visit[]>([]);
  const [monthCreditLoading, setMonthCreditLoading] = useState(false);
  const [monthCreditPage, setMonthCreditPage] = useState(0);
  const [monthCreditRowsPerPage, setMonthCreditRowsPerPage] = useState(10);
  const [monthCreditTotal, setMonthCreditTotal] = useState(0);

  // Overall performance modal states
  const [openOverallPendingBalanceModal, setOpenOverallPendingBalanceModal] = useState(false);
  const [overallPendingBalanceVisits, setOverallPendingBalanceVisits] = useState<Visit[]>([]);
  const [overallPendingBalanceLoading, setOverallPendingBalanceLoading] = useState(false);
  const [overallPendingBalancePage, setOverallPendingBalancePage] = useState(0);
  const [overallPendingBalanceRowsPerPage, setOverallPendingBalanceRowsPerPage] = useState(10);
  const [overallPendingBalanceTotal, setOverallPendingBalanceTotal] = useState(0);

  const [openOverallCreditModal, setOpenOverallCreditModal] = useState(false);
  const [overallCreditVisits, setOverallCreditVisits] = useState<Visit[]>([]);
  const [overallCreditLoading, setOverallCreditLoading] = useState(false);
  const [overallCreditPage, setOverallCreditPage] = useState(0);
  const [overallCreditRowsPerPage, setOverallCreditRowsPerPage] = useState(10);
  const [overallCreditTotal, setOverallCreditTotal] = useState(0);

  // State for today's follow-up patients
  const [todayFollowUpPatients, setTodayFollowUpPatients] = useState<Patient[]>([]);
  const [followUpLoading, setFollowUpLoading] = useState(false);

  const navigate = useNavigate();

  // Fetch today's follow-up patients
  const fetchTodayFollowUpPatients = useCallback(async () => {
    try {
      setFollowUpLoading(true);
      const response = await apiService.getFollowUpPatients();
      
      if (response.success && response.data) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Filter patients with follow-up date for today
        const todayFollowUps = response.data.filter((patient: any) => {
          if (!patient.followUpDate) return false;
          const followUpDate = new Date(patient.followUpDate);
          followUpDate.setHours(0, 0, 0, 0);
          return followUpDate.getTime() === today.getTime();
        });
        
        setTodayFollowUpPatients(todayFollowUps);
        setTodayFollowUpCount(todayFollowUps.length);
      }
    } catch (error) {
      console.error('Error fetching today follow-up patients:', error);
    } finally {
      setFollowUpLoading(false);
    }
  }, []);

  // Memoized fetch function
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const today = getTodayDate();
      const [
        patientsResponse,
        visitsResponse,
        medicinesResponse,
        referencesResponse,
        recentPatientsResponse,
        recentVisitsResponse,
        monthlyPatientsResponse,
        monthlyVisitsResponse,
        todayStatsResponse,
        todayPatientStatsResponse,
        followUpResponse
      ] = await Promise.all([
        apiService.getPatientStats(),
        apiService.getVisitStats(),
        apiService.getMedicineStats(),
        apiService.getReferenceStats(),
        apiService.getPatients({ limit: 5, sortBy: 'date', sortOrder: 'DESC' }),
        apiService.getVisits({ limit: 5, sortBy: 'date', sortOrder: 'DESC' }),
        apiService.getMonthlyPatientStats(),
        apiService.getMonthlyVisitStats(),
        apiService.getVisitStats(today, today),
        apiService.getPatientStats({ dateFrom: today, dateTo: today }),
        apiService.getFollowUpPatients()
      ]);

      if (patientsResponse.success && patientsResponse.data) {
        setStats(prev => ({ ...prev, patients: patientsResponse.data || null }));
      }

      if (visitsResponse.success && visitsResponse.data) {
        setStats(prev => ({ ...prev, visits: visitsResponse.data || null }));
      }

      if (medicinesResponse.success && medicinesResponse.data) {
        setStats(prev => ({ ...prev, medicines: medicinesResponse.data || null }));
      }

      if (referencesResponse.success && referencesResponse.data) {
        setStats(prev => ({ ...prev, references: referencesResponse.data || null }));
      }

      if (recentPatientsResponse.success && recentPatientsResponse.data) {
        setRecentPatients(recentPatientsResponse.data.patients);
      }

      if (recentVisitsResponse.success && recentVisitsResponse.data) {
        setRecentVisits(recentVisitsResponse.data.visits);
      }

      if (monthlyPatientsResponse.success && monthlyPatientsResponse.data) {
        setMonthlyStats(prev => ({ ...prev, patients: monthlyPatientsResponse.data || null }));
      }

      if (monthlyVisitsResponse.success && monthlyVisitsResponse.data) {
        setMonthlyStats(prev => ({ ...prev, visits: monthlyVisitsResponse.data || null }));
      }

      if (todayStatsResponse.success && todayStatsResponse.data) {
        setTodayStats(todayStatsResponse.data);
      }

      if (todayPatientStatsResponse.success && todayPatientStatsResponse.data) {
        setTodayPatientStats(todayPatientStatsResponse.data);
      }

      // Process follow-up patients to count today's follow-ups
      if (followUpResponse.success && followUpResponse.data) {
        console.log('Follow-up response data:', followUpResponse.data);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        console.log('Today date:', today);
        
        // Filter patients with follow-up dates for today or earlier (overdue)
        const overdueFollowUps = followUpResponse.data.filter((patient: any) => {
          if (!patient.followUpDate) return false;
          const followUpDate = new Date(patient.followUpDate);
          followUpDate.setHours(0, 0, 0, 0);
          console.log('Patient follow-up date:', patient.followUpDate, 'Parsed:', followUpDate, 'Is overdue:', followUpDate.getTime() <= today.getTime());
          return followUpDate.getTime() <= today.getTime();
        });
        
        // Sort by follow-up date in descending order (most overdue first)
        overdueFollowUps.sort((a: any, b: any) => {
          const dateA = new Date(a.followUpDate);
          const dateB = new Date(b.followUpDate);
          return dateB.getTime() - dateA.getTime();
        });
        
        console.log('Overdue follow-ups found:', overdueFollowUps.length);
        setTodayFollowUpPatients(overdueFollowUps);
        setTodayFollowUpCount(overdueFollowUps.length);
      }
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Memoized navigation handlers
  const handlePatientView = useCallback((id: number) => {
    navigate(`/patients/${id}`);
  }, [navigate]);

  const handleVisitView = useCallback((visit: Visit) => {
    // Navigate to patient details page with visit ID as query parameter
    navigate(`/patients/${visit.uid}?visitId=${visit.id}`);
  }, [navigate]);

  // Helper to get current month range
  const getCurrentMonthRange = useCallback(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const pad = (n: number) => String(n).padStart(2, '0');
    return {
      dateFrom: `${firstDay.getFullYear()}-${pad(firstDay.getMonth() + 1)}-${pad(firstDay.getDate())}`,
      dateTo: `${lastDay.getFullYear()}-${pad(lastDay.getMonth() + 1)}-${pad(lastDay.getDate())}`
    };
  }, []);

  // Helper to get today's date string (YYYY-MM-DD)
  const getTodayDate = useCallback(() => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }, []);

  // Fetch patients with pending balance
  const fetchPendingBalancePatients = useCallback(async () => {
    try {
      setPendingBalanceLoading(true);
      const { dateFrom, dateTo } = getCurrentMonthRange();
      // Use the accounts transactions endpoint with hasPendingBalance filter and current month range
      const response = await apiService.getTransactions({ 
        hasPendingBalance: 'true',
        page: pendingBalancePage + 1,
        limit: pendingBalanceRowsPerPage,
        dateFrom,
        dateTo
      });
      
      if (response.success && response.data) {
        setPendingBalanceVisits(response.data.transactions);
        setPendingBalanceTotal(response.data.pagination?.totalItems || 0);
      }
    } catch (error) {
      console.error('Failed to fetch pending balance visits:', error);
    } finally {
      setPendingBalanceLoading(false);
    }
  }, [pendingBalancePage, pendingBalanceRowsPerPage, getCurrentMonthRange]);

  // Fetch overall pending balance patients
  const fetchOverallPendingBalancePatients = useCallback(async () => {
    try {
      setOverallPendingBalanceLoading(true);
      // Fetch all transactions with pending balance (no date filter)
      const response = await apiService.getTransactions({ 
        hasPendingBalance: 'true',
        page: overallPendingBalancePage + 1,
        limit: overallPendingBalanceRowsPerPage
      });
      
      if (response.success && response.data) {
        setOverallPendingBalanceVisits(response.data.transactions);
        setOverallPendingBalanceTotal(response.data.pagination?.totalItems || 0);
      }
    } catch (err) {
      console.error('Error fetching overall pending balance patients:', err);
    } finally {
      setOverallPendingBalanceLoading(false);
    }
  }, [overallPendingBalancePage, overallPendingBalanceRowsPerPage]);

  // Fetch overall credit patients
  const fetchOverallCreditPatients = useCallback(async () => {
    try {
      setOverallCreditLoading(true);
      // Fetch all transactions with credit (no date filter)
      const response = await apiService.getTransactions({ 
        hasCredit: 'true',
        page: overallCreditPage + 1,
        limit: overallCreditRowsPerPage
      });
      
      if (response.success && response.data) {
        setOverallCreditVisits(response.data.transactions);
        setOverallCreditTotal(response.data.pagination?.totalItems || 0);
      }
    } catch (err) {
      console.error('Error fetching overall credit patients:', err);
    } finally {
      setOverallCreditLoading(false);
    }
  }, [overallCreditPage, overallCreditRowsPerPage]);

  // Fetch pending balance patients when modal opens
  useEffect(() => {
    if (openPendingBalanceModal) {
      fetchPendingBalancePatients();
    }
  }, [openPendingBalanceModal, fetchPendingBalancePatients]);

  // Fetch overall pending balance patients when overall modal opens
  useEffect(() => {
    if (openOverallPendingBalanceModal) {
      fetchOverallPendingBalancePatients();
    }
  }, [openOverallPendingBalanceModal, fetchOverallPendingBalancePatients]);

  // Fetch overall credit patients when overall modal opens
  useEffect(() => {
    if (openOverallCreditModal) {
      fetchOverallCreditPatients();
    }
  }, [openOverallCreditModal, fetchOverallCreditPatients]);

  // Handle pending balance card click
  const handlePendingBalanceClick = useCallback(() => {
    setOpenPendingBalanceModal(true);
    setPendingBalancePage(0);
  }, []);

  // Handle pending balance modal close
  const handlePendingBalanceModalClose = useCallback(() => {
    setOpenPendingBalanceModal(false);
    setPendingBalanceVisits([]);
    setPendingBalanceTotal(0);
  }, []);

  // Handle overall pending balance modal close
  const handleOverallPendingBalanceModalClose = useCallback(() => {
    setOpenOverallPendingBalanceModal(false);
    setOverallPendingBalanceVisits([]);
    setOverallPendingBalanceTotal(0);
  }, []);

  // Handle overall credit modal close
  const handleOverallCreditModalClose = useCallback(() => {
    setOpenOverallCreditModal(false);
    setOverallCreditVisits([]);
    setOverallCreditTotal(0);
  }, []);

  // Memoized format currency function
  const formatCurrency = useCallback((amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) || 0 : amount;
    return `₹${numAmount % 1 === 0 ? numAmount.toFixed(0) : numAmount.toFixed(2)}`;
  }, []);

  // Handler for today's pending balance card click
  const handleTodayPendingBalanceClick = useCallback(() => {
    setOpenTodayPendingBalanceModal(true);
    setTodayPendingBalancePage(0);
  }, []);

  // Fetch today's pending balance visits
  const fetchTodayPendingBalanceVisits = useCallback(async () => {
    try {
      setTodayPendingBalanceLoading(true);
      const today = getTodayDate();
      const response = await apiService.getTransactions({
        hasPendingBalance: 'true',
        page: todayPendingBalancePage + 1,
        limit: todayPendingBalanceRowsPerPage,
        dateFrom: today,
        dateTo: today
      });
      if (response.success && response.data) {
        setTodayPendingBalanceVisits(response.data.transactions);
        setTodayPendingBalanceTotal(response.data.pagination?.totalItems || 0);
      }
    } catch (error) {
      console.error('Failed to fetch today pending balance visits:', error);
    } finally {
      setTodayPendingBalanceLoading(false);
    }
  }, [todayPendingBalancePage, todayPendingBalanceRowsPerPage, getTodayDate]);

  useEffect(() => {
    if (openTodayPendingBalanceModal) {
      fetchTodayPendingBalanceVisits();
    }
  }, [openTodayPendingBalanceModal, fetchTodayPendingBalanceVisits]);

  const handleTodayPendingBalanceModalClose = useCallback(() => {
    setOpenTodayPendingBalanceModal(false);
    setTodayPendingBalanceVisits([]);
    setTodayPendingBalanceTotal(0);
  }, []);

  // Handlers for credit card clicks
  const handleTodayCreditClick = useCallback(() => {
    setOpenTodayCreditModal(true);
    setTodayCreditPage(0);
  }, []);
  const handleMonthCreditClick = useCallback(() => {
    setOpenMonthCreditModal(true);
    setMonthCreditPage(0);
  }, []);

  // Handlers for overall balance and credit
  const handleOverallPendingBalanceClick = useCallback(() => {
    setOpenOverallPendingBalanceModal(true);
    setOverallPendingBalancePage(0);
  }, []);
  const handleOverallCreditClick = useCallback(() => {
    setOpenOverallCreditModal(true);
    setOverallCreditPage(0);
  }, []);

  // Fetch today's credit visits
  const fetchTodayCreditVisits = useCallback(async () => {
    try {
      setTodayCreditLoading(true);
      const today = getTodayDate();
      const response = await apiService.getTransactions({
        hasCredit: 'true',
        page: todayCreditPage + 1,
        limit: todayCreditRowsPerPage,
        dateFrom: today,
        dateTo: today
      });
      if (response.success && response.data) {
        setTodayCreditVisits(response.data.transactions);
        setTodayCreditTotal(response.data.pagination?.totalItems || 0);
      }
    } catch (error) {
      console.error('Failed to fetch today credit visits:', error);
    } finally {
      setTodayCreditLoading(false);
    }
  }, [todayCreditPage, todayCreditRowsPerPage, getTodayDate]);

  useEffect(() => {
    if (openTodayCreditModal) {
      fetchTodayCreditVisits();
    }
  }, [openTodayCreditModal, fetchTodayCreditVisits]);

  const handleTodayCreditModalClose = useCallback(() => {
    setOpenTodayCreditModal(false);
    setTodayCreditVisits([]);
    setTodayCreditTotal(0);
  }, []);

  // Fetch month credit visits
  const fetchMonthCreditVisits = useCallback(async () => {
    try {
      setMonthCreditLoading(true);
      const { dateFrom, dateTo } = getCurrentMonthRange();
      const response = await apiService.getTransactions({
        hasCredit: 'true',
        page: monthCreditPage + 1,
        limit: monthCreditRowsPerPage,
        dateFrom,
        dateTo
      });
      if (response.success && response.data) {
        setMonthCreditVisits(response.data.transactions);
        setMonthCreditTotal(response.data.pagination?.totalItems || 0);
      }
    } catch (error) {
      console.error('Failed to fetch month credit visits:', error);
    } finally {
      setMonthCreditLoading(false);
    }
  }, [monthCreditPage, monthCreditRowsPerPage, getCurrentMonthRange]);

  useEffect(() => {
    if (openMonthCreditModal) {
      fetchMonthCreditVisits();
    }
  }, [openMonthCreditModal, fetchMonthCreditVisits]);

  const handleMonthCreditModalClose = useCallback(() => {
    setOpenMonthCreditModal(false);
    setMonthCreditVisits([]);
    setMonthCreditTotal(0);
  }, []);

  // Memoized today stat cards data
  const todayStatCards = useMemo(() => [
    {
      title: "New Patients",
      value: todayPatientStats?.totalPatients || 0,
      icon: <People sx={{ fontSize: 30, color: '#1976d2' }} />, 
      color: "#1976d2"
    },
    {
      title: "Today's Visits",
      value: todayStats?.totalVisits || 0,
      icon: <EventNote sx={{ fontSize: 30, color: '#2e7d32' }} />, 
      color: "#2e7d32"
    },
    {
      title: "Today's Revenue",
      value: formatCurrency(Number(todayStats?.totalRevenue || 0)),
      icon: <EventNote sx={{ fontSize: 30, color: '#ed6c02' }} />, 
      color: "#ed6c02"
    },
    {
      title: "Today's Balance",
      value: formatCurrency(Number(todayStats?.totalBalance || 0)),
      icon: <EventNote sx={{ fontSize: 30, color: '#d32f2f' }} />, 
      color: "#d32f2f",
      onClick: handleTodayPendingBalanceClick
    },
    {
      title: "Credit",
      value: formatCurrency(Number(todayStats?.totalCredit || 0)),
      icon: <EventNote sx={{ fontSize: 30, color: '#0288d1' }} />, 
      color: "#0288d1",
      onClick: handleTodayCreditClick
    },
    // {
    //   title: "Today's Follow-ups",
    //   value: todayFollowUpCount,
    //   icon: <Schedule sx={{ fontSize: 30, color: '#f57c00' }} />, 
    //   color: "#f57c00"
    // }
  ], [todayStats, todayPatientStats, formatCurrency, handleTodayPendingBalanceClick, handleTodayCreditClick]);

  // Memoized monthly stat cards data
  const monthlyStatCards = useMemo(() => [
    {
      title: "New Patients",
      currentValue: monthlyStats.patients?.currentMonth?.totalPatients || 0,
      previousValue: monthlyStats.patients?.previousMonth?.totalPatients || 0,
      icon: <People sx={{ fontSize: 30, color: '#1976d2' }} />,
      color: "#1976d2"
    },
    {
      title: "Monthly Visits",
      currentValue: monthlyStats.visits?.currentMonth?.totalVisits || 0,
      previousValue: monthlyStats.visits?.previousMonth?.totalVisits || 0,
      icon: <EventNote sx={{ fontSize: 30, color: '#2e7d32' }} />,
      color: "#2e7d32"
    },
    {
      title: "Monthly Revenue",
      currentValue: Number(monthlyStats.visits?.currentMonth?.totalRevenue) || 0,
      previousValue: Number(monthlyStats.visits?.previousMonth?.totalRevenue) || 0,
      icon: <EventNote sx={{ fontSize: 30, color: '#ed6c02' }} />,
      color: "#ed6c02",
      formatValue: formatCurrency
    },
    {
      title: "Pending Balance",
      currentValue: Number(monthlyStats.visits?.currentMonth?.totalBalance) || 0,
      previousValue: 0,
      icon: <EventNote sx={{ fontSize: 30, color: '#d32f2f' }} />,
      color: "#d32f2f",
      formatValue: formatCurrency,
      onClick: handlePendingBalanceClick
    },
    {
      title: "Credit",
      currentValue: Number(monthlyStats.visits?.currentMonth?.totalCredit ?? 0),
      previousValue: Number(monthlyStats.visits?.previousMonth?.totalCredit ?? 0),
      icon: <EventNote sx={{ fontSize: 30, color: '#0288d1' }} />,
      color: "#0288d1",
      formatValue: formatCurrency,
      onClick: handleMonthCreditClick
    }
  ], [monthlyStats, formatCurrency, handlePendingBalanceClick, handleMonthCreditClick]);

  // Memoized overall stat cards data
  const overallStatCards = useMemo(() => [
    {
      title: "Total Patients",
      value: stats.patients?.totalPatients || 0,
      icon: <People sx={{ fontSize: 30, color: '#1976d2' }} />,
      color: "#1976d2"
    },
    {
      title: "Total Visits",
      value: stats.visits?.totalVisits || 0,
      icon: <EventNote sx={{ fontSize: 30, color: '#2e7d32' }} />,
      color: "#2e7d32"
    },
    {
      title: "Total Revenue",
      value: stats.visits?.totalRevenue || 0,
      icon: <TrendingUp sx={{ fontSize: 30, color: '#f57c00' }} />,
      color: "#f57c00",
      formatValue: formatCurrency
    },
    {
      title: "Pending Balance",
      value: stats.visits?.totalBalance || 0,
      icon: <TrendingDown sx={{ fontSize: 30, color: '#d32f2f' }} />,
      color: "#d32f2f",
      formatValue: formatCurrency,
      onClick: handleOverallPendingBalanceClick
    },
    {
      title: "Total Credit",
      value: stats.visits?.totalCredit || 0,
      icon: <EventNote sx={{ fontSize: 30, color: '#0288d1' }} />,
      color: "#0288d1",
      formatValue: formatCurrency,
      onClick: handleOverallCreditClick
    },
    {
      title: "Total Medicines",
      value: stats.medicines?.totalMedicines || 0,
      icon: <LocalHospital sx={{ fontSize: 30, color: '#388e3c' }} />,
      color: "#388e3c"
    }
  ], [stats, formatCurrency, handleOverallPendingBalanceClick, handleOverallCreditClick]);

  // Debug log for monthly stats
  // console.log('monthlyStats.visits', monthlyStats.visits);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      {/* Today's Statistics */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2, color: '#1976d2', fontWeight: 600 }}>
          Today
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 2 }}>
          {todayStatCards.map((card, index) => (
            <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
              <StatCard title={card.title} value={card.value} icon={card.icon} color={card.color} onClick={card.onClick} />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Overdue Follow-up Patients */}
      {(() => {
        console.log('Rendering follow-up section, count:', todayFollowUpPatients.length);
        return null;
      })()}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2, color: '#1976d2', fontWeight: 600 }}>
          Overdue Follow-up Patients ({todayFollowUpPatients.length})
        </Typography>
        {followUpLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : todayFollowUpPatients.length > 0 ? (
          <Card>
            <CardContent>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Patient Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>OPD</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Age</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Gender</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Follow-up Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Last Visit</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {todayFollowUpPatients.map((patient) => (
                      <TableRow key={patient.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                        <TableCell sx={{ fontWeight: '500' }}>{patient.name}</TableCell>
                        <TableCell sx={{ fontWeight: '500', color: '#1976d2' }}>{patient.opd}</TableCell>
                        <TableCell>{patient.age}</TableCell>
                        <TableCell>
                          <Chip
                            label={patient.gender}
                            size="small"
                            color={patient.gender === 'Male' ? 'primary' : 'secondary'}
                          />
                        </TableCell>
                        <TableCell>
                          {patient.followUpDate ? (() => {
                            const followUpDate = new Date(patient.followUpDate);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            followUpDate.setHours(0, 0, 0, 0);
                            const daysDiff = Math.ceil((followUpDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                            
                            let color: 'success' | 'warning' | 'error' | 'default';
                            let label = format(followUpDate, 'MMM dd, yyyy');
                            
                            if (daysDiff >= 0) {
                              color = 'success'; // Today or future - green
                              label += daysDiff === 0 ? ' (Today)' : ` (In ${daysDiff} day${daysDiff === 1 ? '' : 's'})`;
                            } else if (daysDiff >= -3) {
                              color = 'warning'; // 1-3 days past - yellow
                              label += ` (${Math.abs(daysDiff)} day${Math.abs(daysDiff) === 1 ? '' : 's'} overdue)`;
                            } else {
                              color = 'error'; // More than 3 days past - red
                              label += ` (${Math.abs(daysDiff)} day${Math.abs(daysDiff) === 1 ? '' : 's'} overdue)`;
                            }
                            
                            return (
                              <Chip
                                label={label}
                                size="small"
                                color={color}
                                sx={{ fontWeight: 600 }}
                              />
                            );
                          })() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {patient.last_visit_date ? format(new Date(patient.last_visit_date), 'MMM dd, yyyy') : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Patient Details">
                            <IconButton 
                              size="small" 
                              onClick={() => handlePatientView(patient.id)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent>
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No overdue follow-up appointments.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Monthly Statistics */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2, color: '#1976d2', fontWeight: 600 }}>
          This Month's Performance
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 2 }}>
          {monthlyStatCards.map((card, index) => (
            <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
              <MonthlyStatCard {...card} />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Overall Performance */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2, color: '#1976d2', fontWeight: 600 }}>
          Overall Performance
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {overallStatCards.map((card, index) => (
            <Box key={index} sx={{ flex: '1 1 200px', minWidth: 200, maxWidth: 'calc(33.333% - 16px)' }}>
              <OverallStatCard {...card} />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Recent Data Tables */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* Recent Patients */}
        <Box sx={{ flex: '1 1 500px', minWidth: 500 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Patients
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>OPD</TableCell>
                      <TableCell>Age</TableCell>
                      <TableCell>Gender</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentPatients.map((patient) => (
                      <PatientTableRow 
                        key={patient.id} 
                        patient={patient} 
                        onView={handlePatientView} 
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>

        {/* Recent Visits */}
        <Box sx={{ flex: '1 1 500px', minWidth: 500 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Visits
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Patient</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Fee</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentVisits.map((visit) => (
                      <VisitTableRow 
                        key={visit.id} 
                        visit={visit} 
                        onView={handleVisitView} 
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Pending Balance Modal */}
      <Dialog 
        open={openPendingBalanceModal} 
        onClose={handlePendingBalanceModalClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#9c27b0' }}>
            Visits with Pending Balance
          </Typography>
        </DialogTitle>
        <DialogContent>
          {pendingBalanceLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : pendingBalanceVisits.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No visits with pending balance found.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Patient Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>OPD</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Visit Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Fees</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Balance</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Credit</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Payment Mode</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingBalanceVisits.map((visit) => (
                      <TableRow key={visit.id} hover>
                        <TableCell>{visit.patient_name || 'N/A'}</TableCell>
                        <TableCell>{visit.opd || 'N/A'}</TableCell>
                        <TableCell>{format(new Date(visit.datevisit), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>{visit.time}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#2e7d32' }}>
                          ₹{parseFloat(visit.fee || '0').toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: parseFloat(visit.balance || '0') > 0 ? '#d32f2f' : '#2e7d32' }}>
                          ₹{parseFloat(visit.balance || '0').toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: parseFloat(visit.credit || '0') > 0 ? '#1976d2' : '#2e7d32' }}>
                          ₹{parseFloat(visit.credit || '0').toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={visit.payment_mode || 'N/A'} 
                            size="small" 
                            color={visit.payment_mode === 'Cash' ? 'success' : visit.payment_mode === 'Card' ? 'primary' : 'default'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Patient Details">
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                handlePatientView(parseInt(visit.uid));
                                handlePendingBalanceModalClose();
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={pendingBalanceTotal}
                rowsPerPage={pendingBalanceRowsPerPage}
                page={pendingBalancePage}
                onPageChange={(_, newPage) => setPendingBalancePage(newPage)}
                onRowsPerPageChange={(e) => {
                  setPendingBalanceRowsPerPage(parseInt(e.target.value, 10));
                  setPendingBalancePage(0);
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePendingBalanceModalClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Today's Pending Balance Modal */}
      <Dialog 
        open={openTodayPendingBalanceModal} 
        onClose={handleTodayPendingBalanceModalClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#9c27b0' }}>
            Today's Visits with Pending Balance
          </Typography>
        </DialogTitle>
        <DialogContent>
          {todayPendingBalanceLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : todayPendingBalanceVisits.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No visits with pending balance found for today.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Patient Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>OPD</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Visit Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Fees</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Balance</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Credit</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Payment Mode</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {todayPendingBalanceVisits.map((visit) => (
                      <TableRow key={visit.id} hover>
                        <TableCell>{visit.patient_name || 'N/A'}</TableCell>
                        <TableCell>{visit.opd || 'N/A'}</TableCell>
                        <TableCell>{format(new Date(visit.datevisit), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>{visit.time}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#2e7d32' }}>
                          ₹{parseFloat(visit.fee || '0').toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: parseFloat(visit.balance || '0') > 0 ? '#d32f2f' : '#2e7d32' }}>
                          ₹{parseFloat(visit.balance || '0').toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: parseFloat(visit.credit || '0') > 0 ? '#1976d2' : '#2e7d32' }}>
                          ₹{parseFloat(visit.credit || '0').toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={visit.payment_mode || 'N/A'} 
                            size="small" 
                            color={visit.payment_mode === 'Cash' ? 'success' : visit.payment_mode === 'Card' ? 'primary' : 'default'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Patient Details">
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                handlePatientView(parseInt(visit.uid));
                                handleTodayPendingBalanceModalClose();
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={todayPendingBalanceTotal}
                rowsPerPage={todayPendingBalanceRowsPerPage}
                page={todayPendingBalancePage}
                onPageChange={(_, newPage) => setTodayPendingBalancePage(newPage)}
                onRowsPerPageChange={(e) => {
                  setTodayPendingBalanceRowsPerPage(parseInt(e.target.value, 10));
                  setTodayPendingBalancePage(0);
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTodayPendingBalanceModalClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Today's Credit Modal */}
      <Dialog 
        open={openTodayCreditModal} 
        onClose={handleTodayCreditModalClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#0288d1' }}>
            Today's Visits with Credit
          </Typography>
        </DialogTitle>
        <DialogContent>
          {todayCreditLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : todayCreditVisits.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No visits with credit found for today.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Patient Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>OPD</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Visit Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Fees</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Balance</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Credit</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Payment Mode</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {todayCreditVisits.map((visit) => (
                      <TableRow key={visit.id} hover>
                        <TableCell>{visit.patient_name || 'N/A'}</TableCell>
                        <TableCell>{visit.opd || 'N/A'}</TableCell>
                        <TableCell>{format(new Date(visit.datevisit), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>{visit.time}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#2e7d32' }}>
                          ₹{parseFloat(visit.fee || '0').toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: parseFloat(visit.balance || '0') > 0 ? '#d32f2f' : '#2e7d32' }}>
                          ₹{parseFloat(visit.balance || '0').toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: parseFloat(visit.credit || '0') > 0 ? '#1976d2' : '#2e7d32' }}>
                          ₹{parseFloat(visit.credit || '0').toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={visit.payment_mode || 'N/A'} 
                            size="small" 
                            color={visit.payment_mode === 'Cash' ? 'success' : visit.payment_mode === 'Card' ? 'primary' : 'default'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Patient Details">
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                handlePatientView(parseInt(visit.uid));
                                handleTodayCreditModalClose();
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={todayCreditTotal}
                rowsPerPage={todayCreditRowsPerPage}
                page={todayCreditPage}
                onPageChange={(_, newPage) => setTodayCreditPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setTodayCreditRowsPerPage(parseInt(e.target.value, 10));
                  setTodayCreditPage(0);
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTodayCreditModalClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Month Credit Modal */}
      <Dialog 
        open={openMonthCreditModal} 
        onClose={handleMonthCreditModalClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#0288d1' }}>
            This Month's Visits with Credit
          </Typography>
        </DialogTitle>
        <DialogContent>
          {monthCreditLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : monthCreditVisits.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No visits with credit found for this month.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Patient Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>OPD</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Visit Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Fees</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Balance</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Credit</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Payment Mode</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthCreditVisits.map((visit) => (
                      <TableRow key={visit.id} hover>
                        <TableCell>{visit.patient_name || 'N/A'}</TableCell>
                        <TableCell>{visit.opd || 'N/A'}</TableCell>
                        <TableCell>{format(new Date(visit.datevisit), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>{visit.time}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#2e7d32' }}>
                          ₹{parseFloat(visit.fee || '0').toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: parseFloat(visit.balance || '0') > 0 ? '#d32f2f' : '#2e7d32' }}>
                          ₹{parseFloat(visit.balance || '0').toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: parseFloat(visit.credit || '0') > 0 ? '#1976d2' : '#2e7d32' }}>
                          ₹{parseFloat(visit.credit || '0').toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={visit.payment_mode || 'N/A'} 
                            size="small" 
                            color={visit.payment_mode === 'Cash' ? 'success' : visit.payment_mode === 'Card' ? 'primary' : 'default'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Patient Details">
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                handlePatientView(parseInt(visit.uid));
                                handleMonthCreditModalClose();
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={monthCreditTotal}
                rowsPerPage={monthCreditRowsPerPage}
                page={monthCreditPage}
                onPageChange={(_, newPage) => setMonthCreditPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setMonthCreditRowsPerPage(parseInt(e.target.value, 10));
                  setMonthCreditPage(0);
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMonthCreditModalClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Overall Pending Balance Modal */}
      <Dialog 
        open={openOverallPendingBalanceModal} 
        onClose={handleOverallPendingBalanceModalClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#d32f2f' }}>
            Overall Visits with Pending Balance
          </Typography>
        </DialogTitle>
        <DialogContent>
          {overallPendingBalanceLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Patient Name</TableCell>
                      <TableCell>OPD</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Fee</TableCell>
                      <TableCell>Balance</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overallPendingBalanceVisits.map((visit) => (
                      <TableRow key={visit.id}>
                        <TableCell>{visit.patient_name || 'N/A'}</TableCell>
                        <TableCell>{visit.opd || 'N/A'}</TableCell>
                        <TableCell>{format(new Date(visit.datevisit), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>{formatCurrency(visit.fee)}</TableCell>
                        <TableCell sx={{ color: 'error.main', fontWeight: 600 }}>
                          {formatCurrency(visit.balance)}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Patient Details">
                            <IconButton 
                              size="small"
                              onClick={() => {
                                handlePatientView(parseInt(visit.uid));
                                handleOverallPendingBalanceModalClose();
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={overallPendingBalanceTotal}
                rowsPerPage={overallPendingBalanceRowsPerPage}
                page={overallPendingBalancePage}
                onPageChange={(_, newPage) => setOverallPendingBalancePage(newPage)}
                onRowsPerPageChange={(e) => {
                  setOverallPendingBalanceRowsPerPage(parseInt(e.target.value, 10));
                  setOverallPendingBalancePage(0);
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOverallPendingBalanceModalClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Overall Credit Modal */}
      <Dialog 
        open={openOverallCreditModal} 
        onClose={handleOverallCreditModalClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#0288d1' }}>
            Overall Visits with Credit
          </Typography>
        </DialogTitle>
        <DialogContent>
          {overallCreditLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Patient Name</TableCell>
                      <TableCell>OPD</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Fee</TableCell>
                      <TableCell>Credit</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {overallCreditVisits.map((visit) => (
                      <TableRow key={visit.id}>
                        <TableCell>{visit.patient_name || 'N/A'}</TableCell>
                        <TableCell>{visit.opd || 'N/A'}</TableCell>
                        <TableCell>{format(new Date(visit.datevisit), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>{formatCurrency(visit.fee)}</TableCell>
                        <TableCell sx={{ color: parseFloat(visit.credit || '0') > 0 ? '#1976d2' : '#2e7d32', fontWeight: 600 }}>
                          {formatCurrency(visit.credit)}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Patient Details">
                            <IconButton 
                              size="small"
                              onClick={() => {
                                handlePatientView(parseInt(visit.uid));
                                handleOverallCreditModalClose();
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={overallCreditTotal}
                rowsPerPage={overallCreditRowsPerPage}
                page={overallCreditPage}
                onPageChange={(_, newPage) => setOverallCreditPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setOverallCreditRowsPerPage(parseInt(e.target.value, 10));
                  setOverallCreditPage(0);
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOverallCreditModalClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(Dashboard); 